import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const SUPPORTED = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

/**
 * Push notifications opt-in banner. Rendered inside the notification popover
 * for authenticated users. Handles permission + subscription end-to-end.
 */
export default function PushToggle() {
  const [state, setState] = useState("idle"); // idle | enabled | denied | busy
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!SUPPORTED) return;
    (async () => {
      try {
        if (Notification.permission === "denied") return setState("denied");
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return setState("idle");
        const sub = await reg.pushManager.getSubscription();
        setState(sub ? "enabled" : "idle");
      } catch {}
    })();
  }, []);

  const enable = async () => {
    setError(null); setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? "denied" : "idle");
        return;
      }
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/service-worker.js");
      const { data: keyData } = await api.get("/public/push/vapid-key");
      if (!keyData?.public_key) throw new Error("VAPID non ancora inizializzato lato server");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.public_key),
      });
      await api.post("/me/push/subscribe", sub.toJSON());
      setState("enabled");
    } catch (e) {
      setError(e?.message || "Attivazione fallita");
      setState("idle");
    }
  };

  const disable = async () => {
    setError(null); setState("busy");
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        try { await api.post("/me/push/unsubscribe", { endpoint: sub.endpoint }); } catch {}
        try { await sub.unsubscribe(); } catch {}
      } else {
        try { await api.post("/me/push/unsubscribe", {}); } catch {}
      }
      setState("idle");
    } catch (e) {
      setError(e?.message || "Disattivazione fallita");
      setState("idle");
    }
  };

  if (!SUPPORTED) return null;

  const iconMap = { enabled: BellRing, denied: BellOff, busy: Loader2, idle: Bell };
  const Icon = iconMap[state] || Bell;
  const iconClass = state === "busy" ? "w-4 h-4 animate-spin" : "w-4 h-4";
  const bg = state === "enabled" ? "rgba(59,86,224,0.15)" : "rgba(148,163,184,0.15)";
  const color = state === "enabled" ? "var(--brand)" : "#475569";

  return (
    <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3" data-testid="push-toggle">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg, color }}>
        <Icon className={iconClass} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">Notifiche push</div>
        <div className="text-xs text-slate-500 leading-snug">
          {state === "enabled" && "Attive — riceverai gufate, MVP e sfide anche a schermo spento."}
          {state === "denied" && "Bloccate dal browser. Abilita dalle impostazioni sito, poi riprova."}
          {state === "busy"   && "Attendi qualche istante…"}
          {state === "idle"   && "Ricevi avvisi anche quando FIFE è chiusa (Gufate, MVP, Fantascommesse)."}
        </div>
        {error && <div className="text-[11px] text-red-600 mt-0.5" data-testid="push-error">{error}</div>}
      </div>
      {state !== "denied" && (
        <button
          onClick={state === "enabled" ? disable : enable}
          disabled={state === "busy"}
          data-testid={state === "enabled" ? "push-disable-btn" : "push-enable-btn"}
          className={`shrink-0 rounded-full h-8 px-3 text-xs font-black uppercase tracking-widest transition-colors ${
            state === "enabled"
              ? "bg-slate-200 text-slate-900 hover:bg-slate-300"
              : "btn-brand text-white"
          }`}
        >
          {state === "enabled" ? "Disattiva" : "Attiva"}
        </button>
      )}
    </div>
  );
}
